import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { BLOG_POSTS } from "../data/content";
import { FadeUp, Overline } from "../components/Reveal";
import { CTASection } from "../components/PageHero";

export default function Blog() {
  const [featured, ...rest] = BLOG_POSTS;
  return (
    <main data-testid="blog-page">
      <section className="hospitality-background relative pt-28 sm:pt-32 lg:pt-40 pb-16 overflow-hidden grain-overlay">
        <div className="absolute top-24 -right-32 w-96 h-96 rounded-full bg-saffron-soft blur-3xl opacity-70 drift-c" />
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 relative">
          <FadeUp y={16}>
            <Overline>Hospitality Insights</Overline>
          </FadeUp>
          <h1 className="mt-5 font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-aubergine max-w-3xl">
            Playbooks from the <span className="text-saffron">growth kitchen</span>
          </h1>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-base sm:text-lg text-ink-muted leading-relaxed max-w-2xl">
              Practical, India-first strategies for restaurant marketing, reels, menu psychology, delivery apps and launches. No fluff — just what works.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="hospitality-background pb-24">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
          <FadeUp>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-card"
              className="hospitality-card group grid lg:grid-cols-2 bg-white rounded-[2.5rem] border border-line overflow-hidden hover:shadow-[0_32px_80px_rgba(45,18,77,0.12)] transition-all duration-400"
            >
              <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                <img src={featured.img} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <span className="absolute top-5 left-5 bg-saffron text-aubergine-deep px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">Featured</span>
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-terracotta font-semibold">
                  <span>{featured.category}</span>
                  <span className="text-line">|</span>
                  <span className="text-ink-muted font-normal flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.readMins} min read</span>
                </div>
                <h2 className="mt-4 font-display font-extrabold text-2xl sm:text-3xl text-aubergine leading-snug group-hover:text-terracotta transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-bold text-sm text-aubergine">
                  Read the playbook <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </div>
            </Link>
          </FadeUp>

          <div className="blog-card-grid grid md:grid-cols-2 gap-6 mt-8">
            {rest.map((p, i) => (
              <FadeUp key={p.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="hospitality-card group block h-full bg-white rounded-[2rem] border border-line overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_28px_70px_rgba(45,18,77,0.12)] transition-all duration-400"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-terracotta font-semibold">
                      <span>{p.category}</span>
                    </div>
                    <h3 className="mt-3 font-display font-bold text-lg text-aubergine leading-snug group-hover:text-terracotta transition-colors">{p.title}</h3>
                    <p className="mt-3 text-sm text-ink-muted leading-relaxed line-clamp-2">{p.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between text-xs text-ink-muted">
                      <span>{p.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readMins} min</span>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Reading is good. Growing is better."
        sub="Take one of these playbooks and let us run it for your venue — starting with a free audit."
      />
    </main>
  );
}
